import React from "react";

import InlineSpinner from "components/Graphs/InlineSpinner";

import { SshAccessSetupSection } from "./SshAccessSetupSection";
import type { ComputeNodeNetworkMode } from "./types";

export const SetupStepHeader: React.FC<{
  title: string;
  subtitle?: string;
  step?: number;
  stepTotal?: number;
  onBack?: () => void;
}> = ({ title, subtitle, step, stepTotal = 2, onBack }) => (
  <div className="mb-2">
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-zinc-200">{title}</span>
        {step ? (
          <span className="rounded-full border border-zinc-800 bg-zinc-800 px-[7px] py-[3px] text-[11px] font-semibold text-zinc-400">
            Step {step} of {stepTotal}
          </span>
        ) : null}
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium text-zinc-500"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M8.5 3l-3 3.5 3 3"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back
          </button>
        ) : null}
      </div>
      {subtitle ? (
        <div className="text-sm leading-normal text-zinc-400">{subtitle}</div>
      ) : null}
    </div>
  </div>
);

export const ManagedSetupSection: React.FC<{
  title: string;
  step?: number;
  stepTotal?: number;
  onBack?: () => void;
  children: React.ReactNode;
}> = ({ title, step, stepTotal = 2, onBack, children }) => (
  <div className="pt-8">
    <SetupStepHeader
      title={title}
      step={step}
      stepTotal={stepTotal}
      onBack={onBack}
    />
    {children}
  </div>
);

export const ComputeNodeNetworkModeToggle: React.FC<{
  mode: ComputeNodeNetworkMode;
  isUpdating?: boolean;
  onModeChange: (mode: ComputeNodeNetworkMode) => void;
}> = ({ mode, isUpdating = false, onModeChange }) => {
  const options: Array<{
    value: ComputeNodeNetworkMode;
    label: string;
    description: string;
  }> = [
    {
      value: "isolated",
      label: "Isolated",
      description: "No mesh devices can reach this compute node.",
    },
    {
      value: "meshed",
      label: "Meshed",
      description: "Selected mesh devices can be granted access.",
    },
  ];
  const selectedOption = options.find((option) => option.value === mode);

  return (
    <section>
      <div className="flex items-center gap-2">
        <div
          role="radiogroup"
          aria-label="Compute node network mode"
          className="inline-flex rounded-md border border-zinc-800 bg-zinc-900 p-[3px]"
        >
          {options.map((option) => {
            const selected = mode === option.value;

            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={isUpdating}
                onClick={() => onModeChange(option.value)}
                className={`cursor-pointer rounded-sm border-0 px-3 py-1.5 text-sm transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                  selected
                    ? "bg-zinc-800 font-semibold text-zinc-200"
                    : "bg-transparent font-medium text-zinc-400"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        {isUpdating ? <InlineSpinner className="h-4 w-4" /> : null}
      </div>
      {selectedOption ? (
        <div className="mt-2 text-sm leading-normal text-zinc-500">
          {selectedOption.description}
        </div>
      ) : null}
    </section>
  );
};

export const NetworkModeSetupSection: React.FC<{
  networkMode: ComputeNodeNetworkMode;
  isNetworkModeUpdating?: boolean;
  onNetworkModeChange: (mode: ComputeNodeNetworkMode) => void;
}> = ({ networkMode, isNetworkModeUpdating = false, onNetworkModeChange }) => (
  <ManagedSetupSection title="Select network mode">
    <p className="mb-3 text-sm leading-[1.6] text-zinc-400">
      Choose whether this compute node should stay isolated or join the mesh
      where selected devices can reach it.
    </p>
    <ComputeNodeNetworkModeToggle
      mode={networkMode}
      isUpdating={isNetworkModeUpdating}
      onModeChange={onNetworkModeChange}
    />
  </ManagedSetupSection>
);
export const AccessConfigurationTabContent: React.FC<{
  networkMode: ComputeNodeNetworkMode;
  isNetworkModeUpdating: boolean;
  onNetworkModeChange: (mode: ComputeNodeNetworkMode) => void;
  sshAccess: Array<{ userId: string; linuxUser: string }>;
  isSSHEnabled: boolean;
  usesCertAuthSSHProvider: boolean;
  onCheckSSHAccess: () => Promise<void>;
  onGrantUser: (userId: string, linuxUser: string) => Promise<void>;
  onRevokeUser: (userId: string, linuxUser: string) => Promise<void>;
}> = ({
  networkMode,
  isNetworkModeUpdating,
  onNetworkModeChange,
  sshAccess,
  isSSHEnabled,
  usesCertAuthSSHProvider,
  onCheckSSHAccess,
  onGrantUser,
  onRevokeUser,
}) => (
  <div className="animate-slide-up-240">
    <NetworkModeSetupSection
      networkMode={networkMode}
      isNetworkModeUpdating={isNetworkModeUpdating}
      onNetworkModeChange={onNetworkModeChange}
    />
    <SshAccessSetupSection
      sshAccess={sshAccess}
      isSSHEnabled={isSSHEnabled}
      usesCertAuthSSHProvider={usesCertAuthSSHProvider}
      onCheckSSHAccess={onCheckSSHAccess}
      onGrantUser={onGrantUser}
      onRevokeUser={onRevokeUser}
    />
  </div>
);
